/**
 * EmployerPlanPill.jsx — compact topbar plan/usage chip for the
 * employer workspace.
 *
 * Purpose
 * ───────
 * Candidate workspace shows CreditsBalancePill (Plan + Credits +
 * Free fits). The employer workspace MUST NOT surface credit/wallet
 * language. This component is the operationally-honest employer
 * equivalent:
 *
 *   Base · Usage inactive       (no entitlement row yet)
 *   Focus · 3 / 8 roles         (entitlement row present + cap)
 *   Flow · 12 / ∞ roles         (entitlement row present + unlimited)
 *
 * Pure read. Reads `/api/employer/v1/plan` once per mount; no
 * polling. Links to `/employer/account` for the full Plan & Usage
 * surface. Render is opt-in by AppLayout — only mounted when
 * `currentWorkspace === 'employer'`.
 *
 * STRICT — none of these surfaces shall appear:
 *   - "Credits", "Wallet", "Free fits", "Top up", "Upgrade now",
 *     "Buy more", "Stripe", invoice / overage / receipt.
 *   - Self-serve activation / deactivation affordance.
 *   - Candidate plan codes (`base`, `focus`, `flow` lowercase as
 *     internal codes are fine; surfaced labels stay Base/Focus/Flow/
 *     Enterprise via `employerPlanLabels.planLabel`).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { apiFetch } from '@/lib/api';
import { planLabel, planSubtitle } from '@/lib/employerPlanLabels';


function formatCap(cap) {
  if (cap === null || cap === undefined) return '\u221e'; // ∞
  return String(cap);
}


export default function EmployerPlanPill() {
  const [plan, setPlan]       = useState(null);
  const [loading, setLoading] = useState(true);
  // RC 1.62 plan-pill doctrine (2026-05-29) — track whether the
  // /plan fetch returned 404 ("No organisation yet"). When true,
  // the pill renders nothing (the empty-workspace stub is the
  // single source of truth for the no-org state; the topbar
  // must not contradict it with a misleading plan label).
  const [noOrg, setNoOrg]     = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch('/api/employer/v1/plan');
        if (!cancelled) {
          setPlan(data || null);
          setNoOrg(false);
        }
      } catch (e) {
        // Degrade silently — pill renders placeholder posture …
        // unless the failure is "no organisation", in which case
        // the pill must disappear entirely (doctrine: no plan
        // badge without a workspace).
        if (!cancelled) {
          setPlan(null);
          setNoOrg(e?.status === 404);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Derived ──────────────────────────────────────────────────
  const entitlement   = plan?.entitlement || null;
  const activePlan    = plan?.active_plan || null;
  const tierKey       = entitlement?.plan_tier || activePlan?.plan_tier || null;
  const tierLabelText = planLabel(tierKey);
  const tierSub       = planSubtitle(tierKey);
  // Phase 0.4 had a "placeholder / activation pending" surface
  // that was always a misleading copy under our actual doctrine:
  //   • admin provisions the org as `starter` ACTIVE;
  //   • Stripe upgrades flip atomically on payment clear;
  //   • there is no "pending" state in any normal lifecycle.
  // The catalogue's `placeholder` flag was a Stripe-wiring
  // concern that never belonged on a user's plan pill. Doctrine
  // 2026-05-29 (RC 1.62): permanently retired — no badge, no
  // tooltip line, no caller-is-owner gating because there is no
  // pending state to gate.

  // F.4.4.DOCTRINE-CORRECTION (2026-02-26):
  // The topbar chip must NOT do allowance arithmetic — operator
  // complaint "1 left / 1 role doesn't make sense". The chip now
  // surfaces ONLY plan + activation status. Full allowance pills
  // live on /employer/account ("Account").
  // Tooltip still surfaces the active-roles framing for power users.
  const activeRolesCap  = entitlement?.active_roles_cap
                          ?? activePlan?.active_roles_cap ?? null;
  const activeRolesUsed = entitlement?.active_roles_current ?? null;

  // Tooltip body wording — plain-language remaining.
  let tooltipUsageText;
  if (!entitlement) {
    tooltipUsageText = 'Usage inactive';
  } else if (activeRolesCap === null) {
    tooltipUsageText = 'Unlimited active roles';
  } else {
    const u = (activeRolesUsed === null || activeRolesUsed === undefined)
      ? 0 : Number(activeRolesUsed);
    const remaining = Math.max(0, Number(activeRolesCap) - u);
    const label = remaining === 1 ? 'role' : 'roles';
    tooltipUsageText = `${remaining} active ${label} available`;
  }

  if (loading) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-ndor-edge bg-ndor-surface-alt/40 text-[11px] whitespace-nowrap"
        data-testid="employer-plan-pill"
        data-state="loading"
        aria-label="Employer plan and usage (loading)"
      >
        <Building2 className="h-3.5 w-3.5 text-ndor-ink-muted shrink-0" aria-hidden />
        <span className="font-mono-ui text-ndor-ink-muted">…</span>
      </span>
    );
  }

  // RC 1.62 plan-pill doctrine (2026-05-29) — render NOTHING for
  // users with no organisation. The empty-workspace stub is the
  // single source of truth in that state; the topbar must not
  // contradict it with a misleading "Plan Starter / Activation
  // pending" badge.
  if (noOrg || !plan) {
    return null;
  }

  const tooltipBody = (
    <>
      <p className="text-ndor-ink font-medium mb-1">Account & usage</p>
      <p className="text-ndor-ink-secondary leading-relaxed">
        Plan: <span className="text-ndor-ink">{tierLabelText}</span>
        {tierSub && (<><br/><span className="text-ndor-ink-muted text-[11px]">{tierSub}</span></>)}
        <br/>
        Roles left:{' '}
        <span className="text-ndor-ink">
          {tooltipUsageText}
        </span>
      </p>
      {/* RC 1.62 doctrine: "Activation pending" badge retired.
          The catalogue `placeholder` flag is a Stripe-wiring
          concern that never belonged on a user's plan pill. */}
    </>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to="/employer/account"
          className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 py-1 rounded-md border border-ndor-edge bg-ndor-surface-alt/60 hover:bg-ndor-surface-alt hover:border-ndor-gold/40 transition-colors text-[11px] whitespace-nowrap max-w-full"
          data-testid="employer-plan-pill"
          data-plan-tier={tierKey || ''}
          aria-label="Employer plan and usage"
        >
          <Building2 className="h-3.5 w-3.5 text-ndor-gold shrink-0" aria-hidden />
          <span className="inline-flex items-baseline gap-1">
            <span className="hidden sm:inline font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted text-[10px]">
              Plan
            </span>
            <span
              className="text-ndor-ink font-medium"
              data-testid="employer-plan-pill-tier"
            >
              {tierLabelText}
            </span>
          </span>
          {/* RC 1.62 doctrine: "Activation pending" topbar badge
              retired — no impossible-state UI on a normal account. */}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="bg-ndor-surface border border-ndor-edge text-xs max-w-[280px]">
        {tooltipBody}
      </TooltipContent>
    </Tooltip>
  );
}
